// Data Lab v2 · Products — KPI band + facet filters + hover preview.
// Wraps the same paginated list the v1 uses but surfaces at-a-glance intel
// via a top strip (total / low-stock / out-of-stock / top-sold callout) and
// interactive brand + thickness chip filters that intersect with the search.

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../../components/Layout";
import api from "../../../lib/api";
import { Package, Search, TrendingUp, Zap, PackageX, Sparkles, ArrowUpDown, RefreshCw, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../../../components/ui/hover-card";
import { KpiBand, KpiTile, FacetChips, V2Banner, disableV2 } from "./_shared";
import { T2Chip, T2Stat, T2Panel } from "../../quotes/_t2";
import InfiniteSentinel from "../../../components/InfiniteSentinel";
import AlphaRail from "../../../components/AlphaRail";
import { productTypeLabel, PRODUCT_STATUS_OPTIONS, PRODUCT_TYPE_OPTIONS } from "../../../lib/productEnums";

function GBP(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return `£${v.toFixed(2)}`;
}
function get(obj, path) { return path.split(".").reduce((a, k) => (a == null ? a : a[k]), obj); }

// Ex-VAT sell = BigCommerce `price` (tax-inclusive) ÷ 1.20. Margin is on ex-VAT.
function marginOf(p) {
  const price = Number(get(p, "BCProduct.price"));
  const cost = Number(get(p, "BCProduct.cost_price"));
  if (!(price > 0)) return null;
  const sellEx = price / 1.2;
  if (!(sellEx > 0) || !Number.isFinite(cost)) return null;
  return ((sellEx - cost) / sellEx) * 100;
}
// Data-integrity check — flags rows the quote builder would mis-price.
function integrityOf(p) {
  const price = Number(get(p, "BCProduct.price"));
  const cost = Number(get(p, "BCProduct.cost_price"));
  if (!(price > 0)) return { bad: true, reason: "No sell price" };
  if (!(cost > 0)) return { bad: true, reason: "No cost price" };
  if (cost >= price / 1.2) return { bad: true, reason: "Margin ≤ 0 (cost ≥ sell)" };
  return { bad: false };
}

const COLUMNS = [
  { key: "sku",    label: "SKU",        path: "BCProduct.sku",             sortable: true, w: "w-32" },
  { key: "brand",  label: "Brand",      path: "TMProduct.BrandName",       sortable: true, w: "w-28" },
  { key: "name",   label: "Product",    path: "BCProduct.name",            sortable: true },
  { key: "cost",   label: "Cost",       path: "BCProduct.cost_price",      sortable: true, w: "w-20" },
  { key: "sell",   label: "Sell (inc)", path: "BCProduct.price",           sortable: true, w: "w-24" },
  { key: "retail", label: "Retail",     path: "BCProduct.retail_price",    sortable: true, w: "w-24" },
  { key: "margin", label: "Margin",     path: null,                        sortable: false, w: "w-20" },
  { key: "stock",  label: "Stock",      path: "BCProduct.inventory_level", sortable: true, w: "w-20" },
];

/* Product hover preview — lightweight card next to the SKU cell. */
function ProductHover({ product, children }) {
  const bc = product?.BCProduct || {};
  const tm = product?.TMProduct || {};
  const cf = Object.fromEntries((bc.custom_fields || []).map((f) => [f.name, f.value]));
  return (
    <HoverCard openDelay={140} closeDelay={80}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent align="start" className="border-0 p-0" style={{
        background: "var(--t2-surface2)", border: "1px solid var(--t2-border)",
        borderRadius: 10, padding: 12, width: 320,
        boxShadow: "0 10px 30px rgba(0,0,0,.45)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <div className="t2-mono" style={{ color: "var(--t2-brass)", fontSize: 11, fontWeight: 700 }}>{bc.sku}</div>
          {bc.inventory_level != null && (
            <span className="t2-mono" style={{ fontSize: 10, color: bc.inventory_level <= 0 ? "var(--t2-red)" : "var(--t2-grey3)" }}>
              Stock {bc.inventory_level}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t2-white)", lineHeight: 1.3, marginBottom: 8 }}>
          {bc.name}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
          {tm.BrandName && <T2Chip tone="teal">{tm.BrandName}</T2Chip>}
          {cf["Thickness"] && <T2Chip tone="brass">{cf["Thickness"]}</T2Chip>}
          {cf["Pack Coverage"] && <T2Chip>{cf["Pack Coverage"]}</T2Chip>}
          {cf["Reaction to Fire"] && <T2Chip tone="red">Fire {cf["Reaction to Fire"]}</T2Chip>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
          <T2Stat label="RRP"   value={GBP(bc.price)} />
          <T2Stat label="Cost"  value={GBP(bc.cost_price)} />
          <T2Stat label="Sold"  value={bc.total_sold ?? "—"} />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function LabProductsV2() {
  const navigate = useNavigate();
  const LIMIT = 50;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("BCProduct.total_sold");
  const [order, setOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [insights, setInsights] = useState(null);
  const [brands, setBrands] = useState([]);
  const [thickness, setThickness] = useState([]);
  const [ptype, setPtype] = useState("");     // TMProduct.ProductType filter
  const [pstatus, setPstatus] = useState(""); // TMProduct.Status filter
  const [deletingId, setDeletingId] = useState(null);
  const [letter, setLetter] = useState(null); // A–Z quick-jump (by product name)
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const genRef = useRef(0);

  const effSort = letter ? "BCProduct.name" : sort;
  const effOrder = letter ? "asc" : order;

  const fetchAt = useCallback(async (page, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const gen = genRef.current;
    try {
      const p = new URLSearchParams({ page, limit: LIMIT, sort: effSort, order: effOrder });
      const chipTerm = [...brands, ...thickness].join(" ");
      const combined = [search.trim(), chipTerm].filter(Boolean).join(" ");
      if (combined) p.set("search", combined);
      if (letter) p.set("starts_with", letter);
      if (ptype !== "") p.set("product_type", ptype);
      if (pstatus !== "") p.set("status", pstatus);
      const { data } = await api.get(`/lab/products?${p.toString()}`);
      if (gen !== genRef.current) return;
      const batch = data.items || [];
      const t = data.total || 0;
      setItems((prev) => (append ? [...prev, ...batch] : batch));
      setTotal(t);
      pageRef.current = page;
      setHasMore(page * LIMIT < t);
    } catch {
      if (gen === genRef.current && !append) { setItems([]); setTotal(0); }
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, [effSort, effOrder, search, brands, thickness, letter, ptype, pstatus]);

  const load = useCallback(() => {
    genRef.current += 1;
    pageRef.current = 1;
    loadingRef.current = false;
    setHasMore(true);
    fetchAt(1, false);
  }, [fetchAt]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) fetchAt(pageRef.current + 1, true);
  }, [hasMore, fetchAt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lab/products/insights");
        setInsights(data);
      } catch { setInsights(null); }
    })();
  }, []);

  // Admin row-delete (same pattern as LabListTable).
  const handleDelete = useCallback(async (product) => {
    const id = product._id;
    const label = get(product, "BCProduct.sku") || get(product, "BCProduct.name") || id;
    if (!window.confirm(`Delete this product?\n\n${label}\n\nThis cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/lab/products/${encodeURIComponent(id)}`);
      setItems((prev) => prev.filter((r) => r._id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Delete failed";
      window.alert(`Could not delete row.\n\n${msg}`);
      load();
    } finally {
      setDeletingId(null);
    }
  }, [load]);

  const toggleSort = (col) => {
    if (!col.sortable) return;
    if (sort === col.path) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(col.path); setOrder("desc"); }
  };

  const brandFacets = useMemo(
    () => (insights?.brands || []).slice(0, 12).map((b) => ({ value: b.name, count: b.count })),
    [insights]
  );
  const thicknessFacets = useMemo(
    () => (insights?.thickness_values || []).slice(0, 12).map((t) => ({ value: String(t.value), count: t.count })),
    [insights]
  );
  const activeFilters = brands.length + thickness.length + (search ? 1 : 0) + (ptype !== "" ? 1 : 0) + (pstatus !== "" ? 1 : 0);

  return (
    <Layout>
      <div data-testid="lab-products-v2">
        <V2Banner onDisable={disableV2} />

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <div className="t2-page-eyebrow">
              <Package className="w-3 h-3" /> Data Lab · Products
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", margin: "4px 0 3px" }}>Products</h1>
            <p className="t2-mono" style={{ fontSize: 11.5, color: "var(--t2-grey3)" }}>
              <span style={{ color: "var(--t2-white)" }}>{items.length}</span> of <span style={{ color: "var(--t2-white)" }}>{total}</span> shown
              {activeFilters > 0 && <> · <span style={{ color: "var(--t2-orange)" }}>{activeFilters} filter{activeFilters === 1 ? "" : "s"}</span></>}
            </p>
          </div>
        </div>

        {/* KPI band */}
        <KpiBand testid="products-kpis">
          <KpiTile label="Total"        value={insights?.total ?? "…"}        icon={Package}   testid="kpi-total" />
          <KpiTile label="Out of stock" value={insights?.out_of_stock ?? "…"} icon={PackageX}  tone="red"    testid="kpi-oos" />
          <KpiTile label="Low stock"    value={insights?.low_stock ?? "…"}    icon={Zap}       tone="warn"   testid="kpi-low" />
          <KpiTile label="Brands"       value={insights?.brands?.length ?? "…"} icon={Sparkles} tone="teal"   testid="kpi-brands" />
        </KpiBand>

        {/* Top-sold callout */}
        {insights?.top_sold?.length > 0 && (
          <T2Panel icon={TrendingUp} title="Top sellers"
            right={<span className="t2-mono">Live sync</span>}
            testid="products-top-sellers">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
              {insights.top_sold.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/lab/products/${p.id}`)}
                  data-testid={`top-seller-${p.sku}`}
                  style={{
                    textAlign: "left",
                    padding: "8px 10px",
                    borderRadius: 8,
                    background: "var(--t2-surface2)",
                    border: "1px solid var(--t2-border)",
                    cursor: "pointer",
                    transition: "border-color .12s",
                  }}
                >
                  <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-orange)", fontWeight: 700 }}>{p.sku}</div>
                  <div style={{ fontSize: 12, color: "var(--t2-white)", fontWeight: 600, lineHeight: 1.3, marginTop: 2,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                  <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-brass)", marginTop: 3 }}>
                    {p.brand ? `${p.brand} · ` : ""}<span style={{ color: "var(--t2-white)" }}>{p.sold}</span> sold · {GBP(p.price)}
                  </div>
                </button>
              ))}
            </div>
          </T2Panel>
        )}

        {/* Filter bar */}
        <T2Panel testid="products-filters" title={null}
          style={{ marginTop: 14, padding: 12 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 380 }}>
              <Search className="w-3.5 h-3.5" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--t2-grey3)" }} />
              <input
                className="t2-input"
                value={search}
                onChange={(e) => { setSearch(e.target.value); }}
                placeholder="Search SKU, name, brand…"
                style={{ paddingLeft: 32 }}
                data-testid="search-input"
              />
            </div>
            <select
              className="t2-input"
              value={ptype}
              onChange={(e) => setPtype(e.target.value)}
              data-testid="filter-product-type"
              style={{ maxWidth: 190, fontSize: 12 }}
            >
              <option value="">All Types</option>
              {PRODUCT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="t2-input"
              value={pstatus}
              onChange={(e) => setPstatus(e.target.value)}
              data-testid="filter-product-status"
              style={{ maxWidth: 150, fontSize: 12 }}
            >
              <option value="">All Statuses</option>
              {PRODUCT_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {activeFilters > 0 && (
              <button
                onClick={() => { setSearch(""); setBrands([]); setThickness([]); setPtype(""); setPstatus(""); }}
                className="t2-mono"
                style={{ background: "none", border: "none", color: "var(--t2-red)", cursor: "pointer", fontSize: 11 }}
                data-testid="clear-filters"
              >
                Clear ({activeFilters})
              </button>
            )}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="lab-products-refresh"
              title="Reload from database"
              className="t2-mono"
              style={{
                marginLeft: "auto",
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px",
                borderRadius: 8,
                border: "1px solid var(--t2-border)",
                background: "var(--t2-surface2)",
                color: "var(--t2-grey1)",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: 11,
                opacity: loading ? 0.6 : 1,
              }}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
          {brandFacets.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div className="t2-mono" style={{ fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--t2-brass)", fontWeight: 700, marginBottom: 6 }}>Brands</div>
              <FacetChips options={brandFacets} selected={brands}
                onChange={(sel) => { setBrands(sel); }} testid="brand-facet" />
            </div>
          )}
          {thicknessFacets.length > 0 && (
            <div>
              <div className="t2-mono" style={{ fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--t2-brass)", fontWeight: 700, marginBottom: 6 }}>Thickness</div>
              <FacetChips options={thicknessFacets} selected={thickness}
                onChange={(sel) => { setThickness(sel); }} testid="thickness-facet" />
            </div>
          )}
        </T2Panel>

        {/* Table */}
        <div className="t2-tbl-wrap" style={{ marginTop: 14 }}>
          <table className="t2-tbl" data-testid="products-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.key}
                    onClick={() => toggleSort(c)}
                    className={c.sortable ? "cursor-pointer" : ""}
                    style={{ userSelect: "none" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {c.label}
                      {c.sortable && <ArrowUpDown className="w-3 h-3 opacity-40" />}
                    </span>
                  </th>
                ))}
                <th style={{ textAlign: "right", width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 1} style={{ textAlign: "center", padding: 40, color: "var(--t2-grey3)" }}>Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 1} style={{ textAlign: "center", padding: 40, color: "var(--t2-grey3)" }}>No products match.</td></tr>
              )}
              {items.map((p) => {
                const stock = get(p, "BCProduct.inventory_level");
                const stockLow = stock != null && stock <= 5;
                const stockOut = stock != null && stock <= 0;
                const integ = integrityOf(p);
                const margin = marginOf(p);
                return (
                  <tr key={p._id}
                    onClick={() => navigate(`/lab/products/${p._id}`)}
                    style={{ cursor: "pointer" }}
                    data-testid={`row-${p._id}`}>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <ProductHover product={p}>
                          <span className="t2-mono" style={{
                            color: "var(--t2-orange)", fontSize: 11,
                            borderBottom: "1px dotted rgba(255,104,32,.35)", cursor: "help",
                          }}>{get(p, "BCProduct.sku")}</span>
                        </ProductHover>
                        {integ.bad && (
                          <span title={integ.reason} data-testid={`integrity-flag-${p._id}`}
                            className="t2-mono" style={{ display: "inline-flex", alignItems: "center", gap: 2,
                              fontSize: 9, fontWeight: 700, color: "var(--t2-red)",
                              background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)",
                              borderRadius: 4, padding: "1px 4px", whiteSpace: "nowrap" }}>
                            <AlertTriangle className="w-2.5 h-2.5" /> {integ.reason}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="t2-mono" style={{ color: "var(--t2-teal)", fontSize: 11 }}>{get(p, "TMProduct.BrandName") || "—"}</td>
                    <td style={{ color: "var(--t2-white)" }}>{get(p, "BCProduct.name")}</td>
                    <td className="t2-mono" style={{ color: "var(--t2-grey2)" }}>{GBP(get(p, "BCProduct.cost_price"))}</td>
                    <td className="t2-mono" style={{ color: "var(--t2-white)", fontWeight: 700 }}>{GBP(get(p, "BCProduct.price"))}</td>
                    <td className="t2-mono" style={{ color: "var(--t2-grey2)" }}>{(() => { const r = Number(get(p, "BCProduct.retail_price")); return r > 0 ? GBP(r) : "—"; })()}</td>
                    <td className="t2-mono" style={{ fontWeight: 700,
                      color: margin == null ? "var(--t2-grey3)" : margin >= 6 ? "var(--t2-teal)" : margin >= 3 ? "var(--t2-warn)" : "var(--t2-red)" }}>
                      {margin == null ? "—" : `${margin.toFixed(1)}%`}
                    </td>
                    <td className="t2-mono" style={{ color: stockOut ? "var(--t2-red)" : stockLow ? "var(--t2-warn)" : "var(--t2-grey1)" }}>
                      {stock ?? "—"}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleDelete(p)}
                        disabled={deletingId === p._id}
                        data-testid={`lab-product-delete-${p._id}`}
                        title="Delete this product"
                        style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          padding: 6,
                          borderRadius: 6,
                          border: "1px solid rgba(239, 68, 68, 0.25)",
                          background: "rgba(239, 68, 68, 0.06)",
                          color: "rgb(252, 165, 165)",
                          cursor: deletingId === p._id ? "not-allowed" : "pointer",
                          opacity: deletingId === p._id ? 0.5 : 1,
                        }}
                      >
                        {deletingId === p._id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {items.length > 0 && (
          <InfiniteSentinel
            onReach={loadMore}
            hasMore={hasMore}
            loading={loading}
            count={items.length}
            total={total}
            testId="lab-products-sentinel"
          />
        )}
      </div>
      <AlphaRail active={letter} onPick={setLetter} testId="lab-products-alpha-rail" />
    </Layout>
  );
}
